import Navbar from "../../components/layout/Navbar";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../../components/Button";
import { useState, FormEvent } from "react";
import { useRegisterMutation } from "../../services/authApi";
import { useDispatch } from "react-redux";
import { setCredentials } from "../../features/authSlice";
import { GoogleLoginButton } from "../../components/GoogleAuth/GoogleAuth";
import { Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

const SignUp = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [register, { isLoading }] = useRegisterMutation();

  const [showPassword1, setShowPassword1] = useState(false);
  const [showPassword2, setShowPassword2] = useState(false);
  const [validPhoneNumber, setValidPhoneNumber] = useState(false);

  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    feedback: "",
    color: "",
  });

  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    gender: "male",
    phone_number: "",
    email: "",
    password1: "",
    password2: "",
    rememberMe: false,
    agreeToTerms: true,
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
    if (name === "password1") {
      checkPasswordStrength(value);
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (formData.gender === "") {
      toast.warning("Please select your gender.");
      return;
    }

    try {
      if (!validPhoneNumber) {
        toast.warning("Please enter a valid phone number.");
        return;
      }
      const userData = await register(formData).unwrap();
      // Store email data and token in local storage
      localStorage.setItem("verify-email", formData.email);
      toast.success("Registration successful!", {
        description: "You will be redirected to the verification page.",
      });
      setTimeout(() => {
        navigate("/verify"); // Navigate to verify page after successful registration
      }, 2000);
    } catch (error: unknown) {
      if (typeof error === "object" && error !== null && "data" in error) {
        const err = error as { data?: Record<string, string[]> };
        toast.error(
          Object.entries(err.data as Record<string, string[]>)
            .map(([, value]) => value)
            .join(", ") || "Registration failed: Unknown Error"
        );
      }
      console.error("Registration failed:", error);
    }
  };

  function checkPasswordStrength(password: string) {
    let score = 0; // Initialize score
    let feedback = "";

    // Define weights for different criteria
    const WEIGHTS = {
      length: 0.3, // Heavily weight length
      upperCase: 0.2, // Medium weight for uppercase
      lowerCase: 0.15, // Medium weight for lowercase
      numbers: 0.1, // Medium weight for numbers
      specialChars: 0.2, // Medium weight for special characters
      entropy: 0.2, // Additional entropy weight
    };

    // Criteria checks
    const length = password.length;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumbers = /[0-9]/.test(password);
    const hasSpecialChars = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    // Assign scores based on criteria
    if (length >= 8) score += (WEIGHTS.length * Math.min(16, length)) / 16; // Scale length up to 16 chars
    if (hasUpperCase) score += WEIGHTS.upperCase;
    if (hasLowerCase) score += WEIGHTS.lowerCase;
    if (hasNumbers) score += WEIGHTS.numbers;
    if (hasSpecialChars) score += WEIGHTS.specialChars;

    // Calculate entropy
    const uniqueChars = new Set(password).size;
    const entropy = Math.log2(uniqueChars) * length;
    if (entropy > 50) score += WEIGHTS.entropy;

    // Normalize score to 1
    score = Math.min(1, score);

    // Provide feedback based on the score
    if (score > 0.8) {
      feedback = "Strong password. Great job!";
    } else if (score >= 0.5) {
      feedback = "Moderate password. Consider adding special characters.";
    } else {
      feedback =
        "Weak password. Consider using more length and character variety.";
    }

    setPasswordStrength({
      score: Math.round(score * 100), // Return score as percentage
      feedback,
      color:
        score > 0.8
          ? "text-green-600"
          : score >= 0.5
          ? "text-orange-400"
          : "text-red-400",
    });
  }

  const validatePhoneInput = (phone: string) => {
    const phoneRegex = /^0[789]\d{9}$/;
    setValidPhoneNumber(phoneRegex.test(phone));
  };

  return (
    <div>
      <Navbar />
      <div className="p-5 sm:p-14 flex items-center justify-center landing-bg bg-cover bg-no-repeat bg-center">
        <div className="w-full md:w-fit flex items-center justify-center flex-col bg-white">
          <div className=" sm:p-10 rounded-2xl sm:border border-[#7E57C233]  w-[100%] sm:w-[80%] md:w-fit flex items-center justify-start gap-20 ">
            <div className="flex flex-col gap-2 items-start justify-start w-[500px]">
              <div>
                <h2 className="">Create an account</h2>
                <span>
                  Already have an account?{" "}
                  <Link to="/login">
                    <span className=" underline text-[#7E57C2] font-medium">
                      Log in
                    </span>
                  </Link>
                </span>
              </div>

              <div className="flex flex-col items-center mt-4 justify-center w-full">
                <GoogleLoginButton />
              </div>
              <p className="text-center w-full">OR</p>
              <form
                onSubmit={handleSubmit}
                className="flex flex-col -start justify-center gap-8 w-full"
              >
                <div className="flex items-start justify-center gap-6 flex-col w-full">
                  <div className="flex items-center justify-center gap-3 w-full">
                    <div className="flex items-start justify-center flex-col gap-1.5 w-full">
                      <label htmlFor="firstName" className="text-[#666666CC]">
                        First Name
                      </label>
                      <input
                        type="text"
                        name="first_name"
                        id="firstName"
                        value={formData.first_name}
                        placeholder="John"
                        autoComplete="on"
                        onChange={handleChange}
                        className="h-10 w-full border border-[#66666659] rounded-lg outline-0 px-3"
                        required
                      />
                    </div>
                    <div className="flex items-start justify-center flex-col gap-1.5 w-full">
                      <label htmlFor="lastName" className="text-[#666666CC]">
                        Last Name
                      </label>
                      <input
                        type="text"
                        name="last_name"
                        id="lastName"
                        placeholder="Doe"
                        autoComplete="on"
                        value={formData.last_name}
                        onChange={handleChange}
                        className="h-10 w-full border border-[#66666659] rounded-lg outline-0 px-3"
                        required
                      />
                    </div>
                  </div>
                  <div className="flex items-start flex-col justify-center gap-2">
                    <span className="text-[#666666CC]">
                      What's your gender?
                    </span>
                    <div className="flex items-center justify-center gap-4">
                      <div className="flex items-center justify-center gap-1 w-full">
                        <input
                          type="radio"
                          name="gender"
                          id="female"
                          value="female"
                          checked={formData.gender === "female"}
                          onChange={handleChange}
                          className="accent-[#7E57C2]"
                        />
                        <label htmlFor="female">Female</label>
                      </div>
                      <div className="flex items-center justify-center gap-1 w-full">
                        <input
                          type="radio"
                          name="gender"
                          id="male"
                          value="male"
                          checked={formData.gender === "male"}
                          onChange={handleChange}
                          className="accent-[#7E57C2]"
                        />
                        <label htmlFor="male">Male</label>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start justify-center flex-col gap-1.5 w-full">
                    <label htmlFor="phoneNumber" className="text-[#666666CC]">
                      Phone Number
                    </label>
                    <input
                      type="number"
                      name="phone_number"
                      id="phoneNumber"
                      onBlur={(e) => validatePhoneInput(e.target.value)}
                      placeholder="08012345678"
                      value={formData.phone_number}
                      onChange={handleChange}
                      className="h-10 w-full border border-[#66666659] rounded-lg outline-0 px-3"
                      required
                    />
                  </div>
                  <div className="flex items-start justify-center flex-col gap-1.5 w-full">
                    <label htmlFor="email" className="text-[#666666CC]">
                      Email
                    </label>
                    <input
                      type="email"
                      name="email"
                      id="email"
                      value={formData.email}
                      placeholder="johndoe@gmail.com"
                      onChange={handleChange}
                      className="h-10 w-full border border-[#66666659] rounded-lg outline-0 px-3"
                      required
                    />
                  </div>
                  <div className="flex items-start justify-center flex-col gap-1.5 w-full">
                    <div className="flex items-center relative justify-between w-full">
                      <label htmlFor="password" className="text-[#666666CC]">
                        Password
                      </label>
                    </div>
                    <div className="relative w-full">
                      <input
                        type={showPassword1 ? "text" : "password"}
                        placeholder="Enter your password"
                        name="password1"
                        id="password1"
                        value={formData.password1}
                        onChange={handleChange}
                        className="h-10 w-full border border-[#66666659] rounded-lg outline-0 px-3 pr-10"
                        required
                      />

                      <button
                        type="button"
                        className="absolute right-2 top-1/2 -translate-y-1/2  text-sm"
                        onClick={() => setShowPassword1((prev) => !prev)}
                        tabIndex={-1}
                        aria-label={
                          showPassword1 ? "Hide password" : "Show password"
                        }
                      >
                        {showPassword1 ? <EyeOff /> : <Eye />}
                      </button>
                    </div>
                    {formData.password1 && (
                      <span className={passwordStrength.color}>
                        {passwordStrength.feedback}
                      </span>
                    )}
                  </div>
                  <div className="flex items-start justify-center flex-col gap-1.5 w-full">
                    <div className="flex items-center relative justify-between w-full">
                      <label htmlFor="password" className="text-[#666666CC]">
                        Confirm Password
                      </label>
                    </div>
                    <div className="relative w-full">
                      <input
                        type={showPassword2 ? "text" : "password"}
                        placeholder="Enter your password again"
                        name="password2"
                        id="password2"
                        value={formData.password2}
                        onChange={handleChange}
                        className="h-10 w-full border border-[#66666659] rounded-lg outline-0 px-3"
                        required
                      />

                      <button
                        type="button"
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-sm"
                        onClick={() => setShowPassword2((prev) => !prev)}
                        tabIndex={-1}
                        aria-label={
                          showPassword2 ? "Hide password" : "Show password"
                        }
                      >
                        {showPassword2 ? <EyeOff /> : <Eye />}
                      </button>
                    </div>
                    {formData.password2 &&
                      formData.password1 !== formData.password2 && (
                        <p className="password-strength-weak text-sm">
                          Passwords do not match!
                        </p>
                      )}
                  </div>
                </div>
                <div className="w-full flex items-center justify-between">
                  <span className="hidden sm:block">
                    Already have an account?{" "}
                    <Link to="/login">
                      <span className=" underline text-[#7E57C2] font-medium">
                        Log in
                      </span>
                    </Link>
                  </span>
                  <Button
                    text={isLoading ? "Signing Up..." : "Sign Up"}
                    disabled={isLoading}
                    className="sm:w-fit w-full"
                  />
                </div>
              </form>
            </div>
            <img
              src="/assets/Jellosite-Logo-09.jpg"
              alt=""
              className="w-[250px] hidden md:block"
            />
          </div>
          <ul className="text-[#666666] md:w-full hidden sm:flex gap-3.5 sm:w-[80%]  items-center justify-end mt-1 ">
            <Link to="">
              <li>Help</li>
            </Link>
            <Link to="/privacy_policy">
              <li>Privacy</li>
            </Link>
            <Link to="/terms_conditions">
              <li>Terms</li>
            </Link>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default SignUp;
